import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Loader2, Save, Send, LayoutGrid, FileDown, Building2, CheckCircle2, X, AlertTriangle, Mail,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { ProductGridSelector, type ProductGridItem } from '@/components/shared/ProductGridSelector';
import { PaymentSchedulePanel } from '@/pages/InvoiceEditor/PaymentSchedulePanel';
import { INDIAN_STATES, sellerStateCode, isIntraStateSupply, supplyHint, FALLBACK_SELLER_STATE_CODE } from './invoiceSupply';


const GST_RATES = [0, 5, 12, 18, 28];

interface LineItem {
  id: string;
  description: string;
  hsnSac: string;
  quantity: number;
  rate: number;
  gstRate: number;
  discount: number;
  productId: string | null;
  maxDiscount: number | null;
  discountApprovalEnabled?: boolean;
  gstMode?: 'extra' | 'inclusive' | null;
  // Squad Delta (Apr 2026): if this line is a bundle, server returns
  // customFields.bundleComponents = [{ sku, name, qty, unit, ... }] so the
  // loader view can indent children under parent without refetching.
  customFields?: {
    isBundle?: boolean;
    bundleComponents?: Array<{
      componentProductId: string;
      sku?: string | null;
      name: string;
      qty: number;
      unit?: string | null;
    }>;
    [k: string]: unknown;
  };
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
}

interface Account {
  id: string;
  name: string;
}

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

export function InvoiceEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const fromProposalId = searchParams.get('fromProposal');
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discountOverLimitItems, setDiscountOverLimitItems] = useState<LineItem[]>([]);
  const [raisingDiscountRequest, setRaisingDiscountRequest] = useState(false);
  const [discountRequestSuccess, setDiscountRequestSuccess] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);

  // Form state
  const [contactId, setContactId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [buyerGstin, setBuyerGstin] = useState('');
  const [buyerState, setBuyerState] = useState('');
  // Seller state = company profile stateCode (tour bug B-AC-05); 06 until it loads.
  const [sellerState, setSellerState] = useState<string>(FALLBACK_SELLER_STATE_CODE);
  const [customerType, setCustomerType] = useState('b2c');
  const [invoiceStatus, setInvoiceStatus] = useState<'draft' | 'sent'>('draft');

  const [defaultGstRate, setDefaultGstRate] = useState(18);

  // Inline account creation
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountGstin, setNewAccountGstin] = useState('');
  const [newAccountHasGst, setNewAccountHasGst] = useState<boolean | null>(null);
  const [invGstFetching, setInvGstFetching] = useState(false);
  const [invGstFetched, setInvGstFetched] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  const [lineItems, setLineItems] = useState<LineItem[]>([
    { id: generateId(), description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, discount: 0, productId: null, maxDiscount: null },
  ]);

  // Document-level discount
  const [docDiscountType, setDocDiscountType] = useState<'amount' | 'percentage'>('amount');
  const [docDiscountValue, setDocDiscountValue] = useState<number>(0);
  const [maxDocDiscountPct, setMaxDocDiscountPct] = useState<number>(100);

  // Document-level GST mode
  const [docGstMode, setDocGstMode] = useState<'extra' | 'inclusive'>('extra');

  // Fetch contacts, accounts, and company profile
  useEffect(() => {
    api.get('/contacts').then(res => {
      setContacts(res.data.data?.contacts ?? res.data.data ?? []);
    }).catch(() => {});
    api.get('/accounts?type=customer&limit=100').then(res => {
      setAccounts(res.data.data?.accounts ?? res.data.data ?? []);
    }).catch(() => {});
    // Fetch company profile for default GST rate
    api.get('/settings/company-profile').then(res => {
      const profile = res.data.data;
      setSellerState(sellerStateCode(profile));
      if (profile?.defaultGstRate != null) {
        const rate = Number(profile.defaultGstRate);
        setDefaultGstRate(rate);
        // Update existing line items that still have the hardcoded 18% default (only for new invoices)
        if (isNew && rate !== 18) {
          setLineItems(prev => prev.map(li => li.gstRate === 18 ? { ...li, gstRate: rate } : li));
        }
      }
    }).catch(() => {});
    // Fetch discount policy
    api.get('/settings/discount-policy').then(res => {
      const policy = res.data?.data;
      if (policy?.maxDocumentDiscountPct) setMaxDocDiscountPct(policy.maxDocumentDiscountPct);
      if (policy?.defaultDiscountInputMode) setDocDiscountType(policy.defaultDiscountInputMode as any);
    }).catch(() => {});
  }, []);

  // If editing, load existing invoice
  useEffect(() => {
    if (isNew) return;
    const controller = new AbortController();
    setLoading(true);
    // Replace (not append) — clear first so stale items never show during loading
    setLineItems([]);
    api.get(`/invoices/${id}`, { signal: controller.signal })
      .then(res => {
        const inv = res.data.data;
        setContactId(inv.contactId || '');
        setAccountId(inv.accountId || '');
        setDueDate(inv.dueDate ? inv.dueDate.split('T')[0] : '');
        setBuyerGstin(inv.buyerGstin || '');
        setBuyerState(inv.buyerState || '');
        // Load document-level discount
        if (inv.discountType) setDocDiscountType(inv.discountType);
        if (inv.discountValue != null) setDocDiscountValue(Number(inv.discountValue));
        // Load document-level GST mode
        if (inv.gstMode) setDocGstMode(inv.gstMode as 'extra' | 'inclusive');
        // Backend returns "items" not "lineItems" — check both
        const fetchedItems = inv.items || inv.lineItems;
        if (fetchedItems && fetchedItems.length > 0) {
          // REPLACE (not append) — assign directly, never spread into prev state
          setLineItems(fetchedItems.map((li: any) => ({
            id: li.id || generateId(),
            description: li.description || '',
            hsnSac: li.hsnSac || '',
            quantity: Number(li.quantity) || 1,
            rate: Number(li.rate) || 0,
            gstRate: li.gstRate != null ? Number(li.gstRate) : 18,
            discount: Number(li.discount) || 0,
            productId: li.productId || null,
            maxDiscount: li.maxDiscount ?? null,
            discountApprovalEnabled: li.discountApprovalEnabled !== false,
            gstMode: li.gstMode ?? null,
          })));
        } else {
          // No items from backend — provide one empty row for editing
          setLineItems([{ id: generateId(), description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: defaultGstRate, discount: 0, productId: null, maxDiscount: null, discountApprovalEnabled: true }]);
        }
      })
      .catch(err => {
        if (err.code === 'ERR_CANCELED') return; // aborted by cleanup — not an error
        setError(err.response?.data?.error || 'Failed to load invoice');
      })
      .finally(() => setLoading(false));
    // Cleanup: abort the in-flight request when id changes or component unmounts.
    // Without this, a stale response from invoice A could resolve after invoice B
    // loads and overwrite B's items — producing the duplicate line-item bug.
    return () => controller.abort();
  }, [id, isNew]);

  // Load from proposal (Convert to Invoice)
  useEffect(() => {
    if (!fromProposalId || !isNew) return;
    api.get(`/proposals/${fromProposalId}`)
      .then(res => {
        const p = res.data.data;
        setContactId(p.contactId || '');
        setAccountId(p.accountId || '');
        // Load buyerState and lineItems from proposal content
        const content = typeof p.content === 'object' && p.content ? p.content : {};
        if (content.buyerState) setBuyerState(content.buyerState);
        if (content.notes) {
          // notes field is not in form state yet but buyer GSTIN might be derived
        }
        // Load gstMode from proposal
        if (p.gstMode) setDocGstMode(p.gstMode as 'extra' | 'inclusive');
        const proposalItems = content.lineItems || p.lineItems;
        if (proposalItems && proposalItems.length > 0) {
          // Filter out items with empty descriptions to prevent validation errors
          const validItems = proposalItems
            .map((li: any) => ({
              id: generateId(),
              description: (li.description || '').trim(),
              hsnSac: li.hsnSac || '',
              quantity: Number(li.quantity) || 1,
              rate: Number(li.rate) || 0,
              gstRate: li.gstRate != null ? Number(li.gstRate) : 18,
              gstMode: li.gstMode ?? null,
            }))
            .filter((li: LineItem) => li.description !== '');
          if (validItems.length > 0) {
            setLineItems(validItems);
          }
        }
      })
      .catch(() => {});
  }, [fromProposalId, isNew]);

  const [invGstEnrichment, setInvGstEnrichment] = useState<any>(null);

  // GST lookup for inline account creation
  const handleInvoiceGstLookup = async (gstin: string) => {
    if (gstin.length !== 15) return;
    setInvGstFetching(true);
    setInvGstFetched(false);
    setInvGstEnrichment(null);
    try {
      const res = await api.get(`/accounts/gst-lookup/${gstin}`);
      const d = res.data.data;
      if (d.companyName || d.tradeName || d.legalName) {
        setNewAccountName(d.companyName || d.tradeName || d.legalName);
      }
      // Auto-fill buyer GSTIN and state
      if (d.stateCode) setBuyerState(d.stateCode);
      setBuyerGstin(gstin);
      if (d.source === 'api') {
        setInvGstEnrichment({
          tradeName: d.tradeName || null,
          legalName: d.legalName || null,
          gstStatus: d.gstStatus || null,
          gstRegistrationDate: d.registrationDate || null,
          gstBusinessType: d.businessType || null,
          gstLastUpdated: d.lastUpdated || null,
          gstRawData: d.rawData || null,
        });
      }
      setInvGstFetched(true);
    } catch {
      // Fallback — user can still type name manually
    } finally {
      setInvGstFetching(false);
    }
  };

  // Create new account inline from invoice editor
  const handleInvoiceCreateAccount = async () => {
    if (!newAccountName.trim()) return;
    setCreatingAccount(true);
    try {
      const payload: any = { name: newAccountName.trim(), type: 'customer' };
      if (newAccountGstin.length === 15) payload.gstin = newAccountGstin;
      if (invGstEnrichment) Object.assign(payload, invGstEnrichment);
      const res = await api.post('/accounts', payload);
      const newAcc = res.data.data;
      setAccounts(prev => [...prev, { id: newAcc.id, name: newAcc.name }]);
      setAccountId(newAcc.id);
      // Auto-link selected Contact to this new Account (B2B linking)
      if (contactId) {
        try {
          await api.post('/accounts/link-contact', { contactId, accountId: newAcc.id });
        } catch { /* non-critical — Account created, link is best-effort */ }
      }
      setNewAccountName('');
      setNewAccountGstin('');
      setNewAccountHasGst(null);
      setInvGstFetched(false);
      setShowNewAccount(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create account');
    } finally {
      setCreatingAccount(false);
    }
  };

  // GST calculation logic
  const isIntraState = isIntraStateSupply(buyerState, sellerState);

  const calculations = useMemo(() => {
    let subtotal = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;
    let totalItemDiscount = 0;

    const itemCalcs = lineItems.map(item => {
      const grossAmount = Number((item.quantity * item.rate).toFixed(2));
      const discountAmount = Number((grossAmount * (item.discount || 0) / 100).toFixed(2));
      const taxableAmount = Number((grossAmount - discountAmount).toFixed(2));
      const gstAmount = Number(((taxableAmount * item.gstRate) / 100).toFixed(2));
      let cgst = 0;
      let sgst = 0;
      let igst = 0;

      if (buyerState) {
        if (isIntraState) {
          cgst = Number((gstAmount / 2).toFixed(2));
          sgst = Number((gstAmount / 2).toFixed(2));
        } else {
          igst = gstAmount;
        }
      }

      subtotal += taxableAmount;
      totalCgst += cgst;
      totalSgst += sgst;
      totalIgst += igst;
      totalItemDiscount += discountAmount;

      return { grossAmount, discountAmount, taxableAmount, cgst, sgst, igst, total: Number((taxableAmount + gstAmount).toFixed(2)) };
    });

    subtotal = Number(subtotal.toFixed(2));
    totalCgst = Number(totalCgst.toFixed(2));
    totalSgst = Number(totalSgst.toFixed(2));
    totalIgst = Number(totalIgst.toFixed(2));
    totalItemDiscount = Number(totalItemDiscount.toFixed(2));

    // Document-level discount
    let docDiscountAmt = 0;
    let docDiscountPct = 0;
    if (docDiscountType === 'percentage') {
      docDiscountPct = docDiscountValue;
      docDiscountAmt = Number((subtotal * docDiscountValue / 100).toFixed(2));
    } else {
      docDiscountAmt = Number(docDiscountValue.toFixed(2));
      docDiscountPct = subtotal > 0 ? Number((docDiscountValue / subtotal * 100).toFixed(2)) : 0;
    }
    const afterDocDiscount = Number((subtotal - docDiscountAmt).toFixed(2));

    const totalTax = Number((totalCgst + totalSgst + totalIgst).toFixed(2));
    const grandTotalRaw = Number((afterDocDiscount + totalTax).toFixed(2));
    const grandTotal = Math.round(grandTotalRaw);
    const roundOff = Number((grandTotal - grandTotalRaw).toFixed(2));

    return { subtotal, totalCgst, totalSgst, totalIgst, totalTax, roundOff, grandTotal, itemCalcs, totalItemDiscount, docDiscountAmt, docDiscountPct, afterDocDiscount };
  }, [lineItems, buyerState, isIntraState, docDiscountType, docDiscountValue]);

  const [showProductGrid, setShowProductGrid] = useState(false);
  // Squad Delta (Apr 2026) — bundle render mode. `collapse` = customer-facing
  // (one row per kit). `expand` = loader-facing (show each component). Affects
  // display only; saved invoice always keeps one line per kit (the snapshot on
  // `customFields.bundleComponents` drives the expand view).
  const [bundleRenderMode, setBundleRenderMode] = useState<'collapse' | 'expand'>('collapse');

  const addLineItem = () => {
    setLineItems([...lineItems, { id: generateId(), description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: defaultGstRate, discount: 0, productId: null, maxDiscount: null }]);
  };

  const handleProductSelect = (product: ProductGridItem) => {
    setLineItems(prev => [
      ...prev,
      {
        id: generateId(),
        description: product.name,
        hsnSac: product.hsnCode || '',
        quantity: 1,
        rate: product.unitPrice,
        gstRate: product.gstRate,
        discount: 0,
        productId: product.id,
        maxDiscount: product.maxDiscount ?? null,
      },
    ]);
  };

  const removeLineItem = (itemId: string) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter(li => li.id !== itemId));
  };

  const updateLineItem = (itemId: string, field: keyof LineItem, value: string | number) => {
    setLineItems(lineItems.map(li => li.id === itemId ? { ...li, [field]: value } : li));
  };

  const handleRaiseDiscountRequest = async () => {
    if (discountOverLimitItems.length === 0) return;
    try {
      setRaisingDiscountRequest(true);
      setError(null);
      for (const li of discountOverLimitItems) {
        await api.post('/discount-requests', {
          invoiceId: isNew ? undefined : id,
          accountId: accountId || undefined,
          contactId: contactId || undefined,
          productId: li.productId || undefined,
          itemDescription: li.description,
          itemRate: li.rate,
          itemQuantity: li.quantity,
          requestedPct: li.discount,
          reason: `Discount of ${li.discount}% requested on "${li.description}" (max allowed: ${li.maxDiscount}%)`,
          discountScope: 'product',
          documentType: 'invoice',
          documentId: isNew ? undefined : id,
        });
      }
      setDiscountRequestSuccess(true);
      setDiscountOverLimitItems([]);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to raise discount request');
    } finally {
      setRaisingDiscountRequest(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // ── Discount validation gate ──
      const effDocDiscountPct = calculations.docDiscountPct ?? 0;
      if (effDocDiscountPct > maxDocDiscountPct) {
        setError(`Document discount of ${effDocDiscountPct.toFixed(1)}% exceeds max allowed ${maxDocDiscountPct}%. Please reduce or request approval.`);
        setSaving(false);
        return;
      }

      const overLimitItems = lineItems.filter(li =>
        li.description.trim() !== '' &&
        li.discount > 0 &&
        li.maxDiscount !== null &&
        li.discount > li.maxDiscount &&
        li.discountApprovalEnabled !== false
      );
      if (overLimitItems.length > 0) {
        const names = overLimitItems.map(li => `"${li.description}" (${li.discount}% > max ${li.maxDiscount}%)`).join(', ');
        setDiscountOverLimitItems(overLimitItems);
        setDiscountRequestSuccess(false);
        setError(`Discount exceeds maximum allowed for: ${names}. Raise a discount request for approval.`);
        setSaving(false);
        return;
      }
      setDiscountOverLimitItems([]);

      // Filter out empty line items (no description) to avoid backend validation errors
      const validItems = lineItems.filter(li => li.description.trim() !== '');
      if (validItems.length === 0) {
        setError('At least one line item with a description is required');
        setSaving(false);
        return;
      }

      // Apr 23, 2026 (Bug 2) — backend enum is 'percentage' | 'fixed'. Frontend
      // has historically used 'amount' as the label for a flat-₹ discount,
      // which was making every submit fail Zod validation with a generic
      // "Validation error" toast even though nothing on screen looked wrong.
      const apiDiscountType: 'percentage' | 'fixed' | undefined = docDiscountValue > 0
        ? (docDiscountType === 'percentage' ? 'percentage' : 'fixed')
        : undefined;

      const payload = {
        contactId: contactId || undefined,
        accountId: accountId || undefined,
        dueDate: dueDate || undefined,
        buyerGstin: buyerGstin || undefined,
        buyerState: buyerState || undefined,
        // Only send status on CREATE — edit uses the inline dropdown on the list page
        ...(isNew && { status: invoiceStatus }),
        // Document-level GST mode
        gstMode: docGstMode,
        // Document-level discount
        discountType: apiDiscountType,
        discountValue: docDiscountValue,
        discountPct: calculations.docDiscountPct,
        discountAmount: calculations.docDiscountAmt,
        lineItems: validItems.map(li => ({
          description: li.description.trim(),
          hsnSac: li.hsnSac || undefined,
          quantity: li.quantity,
          rate: li.rate,
          gstRate: li.gstRate,
          discount: li.discount || 0,
          productId: li.productId || undefined,
          gstMode: li.gstMode ?? docGstMode,
        })),
      };

      if (isNew) {
        const res = await api.post('/invoices', payload);
        const newId = res.data.data?.id;
        navigate(newId ? `/invoices/${newId}` : '/invoices', { replace: true });
      } else {
        await api.put(`/invoices/${id}`, payload);
        navigate('/invoices');
      }
    } catch (err: any) {
      // Handle 422 mixed_gst_mode
      if (err.response?.status === 422 && err.response?.data?.error === 'mixed_gst_mode') {
        const d = err.response.data;
        setError(`Mixed GST mode: ${d.inclusiveCount} inclusive and ${d.extraCount} extra line(s). Align all lines to the same mode before saving.`);
        setSaving(false);
        return;
      }
      // Apr 23, 2026 (Bug 2) — backend errorHandler returns `details: [{field, message}]`
      // for ZodError; surface those so users can see "buyerGstin is invalid" rather
      // than a generic "Validation error". Falls through to plain error message otherwise.
      const resp = err.response?.data;
      const details: Array<{ field: string; message: string }> = Array.isArray(resp?.details) ? resp.details : [];
      if (details.length > 0) {
        const msg = details.map(d => `${d.field || 'field'}: ${d.message}`).join(' · ');
        setError(`${resp?.error || 'Validation error'} — ${msg}`);
      } else {
        setError(resp?.error || 'Failed to save invoice');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading invoice...</span>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      {/* Back button */}
      <button
        onClick={() => navigate('/invoices')}
        className="mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft size={16} />
        Back to Invoices
      </button>

      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {isNew ? 'Create Invoice' : 'Edit Invoice'}
        </h1>
        <div className="flex items-center gap-2">
          {!isNew && (
            <>
              <button
                onClick={() => window.open(`/api/v1/invoices/${id}/pdf`, '_blank')}
                className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <FileDown size={16} />
                Download PDF
              </button>
              <button
                onClick={() => {
                  // Pre-fill to with contact email
                  const contact = contacts.find(c => c.id === contactId);
                  setEmailTo(contact?.email || '');
                  setEmailSent(false);
                  setShowEmailModal(true);
                }}
                className="flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
              >
                <Mail size={16} />
                Send Email
              </button>
            </>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={16} />}
            {isNew ? 'Create Invoice' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Send Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Send Invoice via Email</h3>
              <button onClick={() => setShowEmailModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            {emailSent ? (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                <CheckCircle2 size={32} className="mx-auto mb-2 text-green-600" />
                <p className="font-medium text-green-800">Invoice sent successfully!</p>
                <button onClick={() => setShowEmailModal(false)} className="mt-3 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700">Done</button>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Recipient Email *</label>
                  <input
                    type="email"
                    value={emailTo}
                    onChange={e => setEmailTo(e.target.value)}
                    placeholder="customer@example.com"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <p className="mb-4 text-xs text-gray-500">Invoice PDF will be attached automatically.</p>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowEmailModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button
                    disabled={!emailTo || emailSending}
                    onClick={async () => {
                      setEmailSending(true);
                      try {
                        await api.post(`/invoices/${id}/send-email`, { to: emailTo });
                        setEmailSent(true);
                      } catch (err: any) {
                        setError(err.response?.data?.error || 'Failed to send email');
                        setShowEmailModal(false);
                      } finally {
                        setEmailSending(false);
                      }
                    }}
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {emailSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {emailSending ? 'Sending...' : 'Send Invoice'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <p>{error}</p>
              {discountOverLimitItems.length > 0 && (
                <button
                  onClick={handleRaiseDiscountRequest}
                  disabled={raisingDiscountRequest}
                  className="mt-2 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {raisingDiscountRequest ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {raisingDiscountRequest ? 'Raising Request...' : `Raise Discount Request (${discountOverLimitItems.length} item${discountOverLimitItems.length > 1 ? 's' : ''})`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {discountRequestSuccess && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <p className="font-medium">Discount request raised successfully!</p>
          <p className="mt-1">Your request has been sent to admin/sales head for approval. You'll be notified once approved.</p>
        </div>
      )}

      {/* Invoice Details */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Invoice Details</h2>
          {/* Document-level GST mode toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">GST Mode:</span>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
              <button
                type="button"
                onClick={() => setDocGstMode('extra')}
                className={`px-3 py-1.5 transition-colors ${docGstMode === 'extra' ? 'bg-[var(--saffron,#f59e0b)] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Extra (+GST)
              </button>
              <button
                type="button"
                onClick={() => setDocGstMode('inclusive')}
                className={`px-3 py-1.5 border-l border-gray-200 transition-colors ${docGstMode === 'inclusive' ? 'bg-[var(--saffron,#f59e0b)] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Inclusive
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Contact</label>
            <select
              value={contactId}
              onChange={(e) => {
                const cid = e.target.value;
                setContactId(cid);
                // Auto-fill GSTIN from contact
                if (cid) {
                  api.get(`/contacts/${cid}`).then(res => {
                    const c = res.data.data;
                    if (c?.customerType) setCustomerType(c.customerType);
                    if (c?.gstin && !buyerGstin) setBuyerGstin(c.gstin);
                    if (c?.stateCode && !buyerState) setBuyerState(c.stateCode);
                  }).catch(() => {});
                }
              }}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            >
              <option value="">Select Contact</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Account</label>
              <button
                type="button"
                onClick={() => { setShowNewAccount(!showNewAccount); setNewAccountHasGst(null); setNewAccountName(''); setNewAccountGstin(''); setInvGstFetched(false); }}
                className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]"
              >
                {showNewAccount ? <X size={12} /> : <><Building2 size={12} /><Plus size={10} /></>}
                {showNewAccount ? 'Cancel' : 'New'}
              </button>
            </div>
            {!showNewAccount ? (
              <select
                value={accountId}
                onChange={(e) => {
                  const aid = e.target.value;
                  setAccountId(aid);
                  if (aid) {
                    api.get(`/accounts/${aid}`).then(res => {
                      const a = res.data.data;
                      if (a?.gstin && !buyerGstin) setBuyerGstin(a.gstin);
                      if (a?.stateCode && !buyerState) setBuyerState(a.stateCode);
                    }).catch(() => {});
                  }
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              >
                <option value="">Select Account</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            ) : (
              <div className="space-y-2 rounded-lg border border-[var(--ink-3)] bg-[var(--saffron-50)]/30 p-3">
                {newAccountHasGst === null ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Has GSTIN?</span>
                    <button type="button" onClick={() => setNewAccountHasGst(true)} className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 hover:bg-green-200">Yes</button>
                    <button type="button" onClick={() => setNewAccountHasGst(false)} className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-200">No</button>
                  </div>
                ) : newAccountHasGst ? (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Enter GSTIN"
                        value={newAccountGstin}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase().slice(0, 15);
                          setNewAccountGstin(v);
                          if (v.length === 15) handleInvoiceGstLookup(v);
                        }}
                        maxLength={15}
                        className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs focus:border-[var(--ink-0)] focus:outline-none"
                      />
                      {invGstFetching && <Loader2 size={14} className="animate-spin text-[var(--saffron-600)]" />}
                      {invGstFetched && <CheckCircle2 size={14} className="text-green-500" />}
                    </div>
                    <input
                      type="text"
                      placeholder="Company Name"
                      value={newAccountName}
                      onChange={(e) => setNewAccountName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleInvoiceCreateAccount()}
                      className={`w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-[var(--ink-0)] focus:outline-none ${invGstFetched ? 'bg-green-50 border-green-300' : ''}`}
                    />
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={handleInvoiceCreateAccount} disabled={!newAccountName.trim() || creatingAccount} className="rounded bg-[var(--ink-0)] px-3 py-1 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                        {creatingAccount ? 'Creating...' : 'Create'}
                      </button>
                      <button type="button" onClick={() => setNewAccountHasGst(null)} className="text-xs text-gray-500 hover:text-gray-700">← Back</button>
                    </div>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Company Name"
                      value={newAccountName}
                      onChange={(e) => setNewAccountName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleInvoiceCreateAccount()}
                      className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-[var(--ink-0)] focus:outline-none"
                    />
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={handleInvoiceCreateAccount} disabled={!newAccountName.trim() || creatingAccount} className="rounded bg-[var(--ink-0)] px-3 py-1 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                        {creatingAccount ? 'Creating...' : 'Create'}
                      </button>
                      <button type="button" onClick={() => setNewAccountHasGst(null)} className="text-xs text-gray-500 hover:text-gray-700">← Back</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Customer Type</label>
            <select
              value={customerType}
              onChange={(e) => setCustomerType(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            >
              <option value="b2c">B2C — Individual</option>
              <option value="b2b">B2B — Business</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Buyer GSTIN</label>
            <input
              type="text"
              value={buyerGstin}
              onChange={(e) => setBuyerGstin(e.target.value.toUpperCase())}
              placeholder="e.g. 22AAAAA0000A1Z5"
              maxLength={15}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Buyer State (Place of Supply)</label>
            <select
              value={buyerState}
              onChange={(e) => setBuyerState(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            >
              <option value="">Select State</option>
              {INDIAN_STATES.map(s => (
                <option key={s.code} value={s.code}>{s.code} - {s.name}</option>
              ))}
            </select>
          </div>
          {isNew && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
              <select
                value={invoiceStatus}
                onChange={(e) => setInvoiceStatus(e.target.value as 'draft' | 'sent')}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
              </select>
            </div>
          )}
        </div>
        {buyerState && (
          <p className="mt-3 text-xs text-gray-500">
            {supplyHint(buyerState, sellerState)}
          </p>
        )}
      </div>

      {/* Line Items */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Line Items</h2>
          <div className="flex items-center gap-2">
            {/* Squad Delta: bundle render toggle — customer vs loader view */}
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-0.5 text-xs">
              <button
                onClick={() => setBundleRenderMode('collapse')}
                className={`px-2 py-1 rounded ${
                  bundleRenderMode === 'collapse' ? 'bg-gray-900 text-white' : 'text-gray-600'
                }`}
                title="Customer view: kit appears as one line"
              >
                Customer
              </button>
              <button
                onClick={() => setBundleRenderMode('expand')}
                className={`px-2 py-1 rounded ${
                  bundleRenderMode === 'expand' ? 'bg-gray-900 text-white' : 'text-gray-600'
                }`}
                title="Loader view: kit components listed"
              >
                Loader
              </button>
            </div>
            <button
              onClick={() => setShowProductGrid(true)}
              className="flex items-center gap-1 rounded-lg border border-[var(--ink-3)] bg-[var(--saffron-50)] px-3 py-1.5 text-sm font-medium text-[var(--ink-0)] hover:bg-[var(--saffron-50)]"
            >
              <LayoutGrid size={14} />
              Browse Products
            </button>
            <button
              onClick={addLineItem}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus size={14} />
              Add Item
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-2 py-2 w-[24%]">Description</th>
                <th className="px-2 py-2 w-[8%]">HSN/SAC</th>
                <th className="px-2 py-2 w-[6%] text-right">Qty</th>
                <th className="px-2 py-2 w-[10%] text-right">Rate (INR)</th>
                <th className="px-2 py-2 w-[8%] text-center">Disc %</th>
                <th className="px-2 py-2 w-[8%] text-center">GST %</th>
                <th className="px-2 py-2 w-[6%] text-center">Mode</th>
                {buyerState && isIntraState && (
                  <>
                    <th className="px-2 py-2 w-[10%] text-right">CGST</th>
                    <th className="px-2 py-2 w-[10%] text-right">SGST</th>
                  </>
                )}
                {buyerState && !isIntraState && (
                  <th className="px-2 py-2 w-[10%] text-right">IGST</th>
                )}
                <th className="px-2 py-2 w-[12%] text-right">Total</th>
                <th className="px-2 py-2 w-[4%]"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => {
                const calc = calculations.itemCalcs[idx];
                return (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        placeholder="Item description"
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={item.hsnSac}
                        onChange={(e) => updateLineItem(item.id, 'hsnSac', e.target.value)}
                        placeholder="HSN"
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-right text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => updateLineItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-right text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step="0.5"
                          value={item.discount || ''}
                          onChange={(e) => updateLineItem(item.id, 'discount', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className={`w-full rounded border px-2 py-1.5 text-center text-sm focus:outline-none ${
                            item.discount > 0 && item.maxDiscount !== null && item.discount > item.maxDiscount && item.discountApprovalEnabled !== false
                              ? 'border-red-400 bg-red-50 text-red-700 focus:border-red-500'
                              : 'border-gray-200 focus:border-[var(--ink-0)]'
                          }`}
                          title={item.discountApprovalEnabled === false ? 'No approval required' : (item.maxDiscount !== null ? `Max allowed: ${item.maxDiscount}%` : 'No limit set')}
                        />
                        {item.maxDiscount !== null && item.discountApprovalEnabled !== false && (
                          <span className={`absolute -bottom-3.5 left-0 text-[10px] ${
                            item.discount > item.maxDiscount ? 'text-red-500 font-medium' : 'text-gray-400'
                          }`}>
                            max {item.maxDiscount}%
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <select
                        value={item.gstRate}
                        onChange={(e) => updateLineItem(item.id, 'gstRate', parseInt(e.target.value))}
                        className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-center text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      >
                        {GST_RATES.map(r => (
                          <option key={r} value={r}>{r}%</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {/* Per-line GST mode chip — amber when differs from doc mode */}
                      {(() => {
                        const lineMode = item.gstMode ?? docGstMode;
                        const differs = lineMode !== docGstMode;
                        return (
                          <button
                            type="button"
                            onClick={() => updateLineItem(item.id, 'gstMode', lineMode === 'extra' ? 'inclusive' : 'extra')}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${
                              differs
                                ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-400'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            title="Click to toggle this line's GST mode"
                          >
                            {lineMode === 'extra' ? '+GST' : 'incl.'}
                          </button>
                        );
                      })()}
                    </td>
                    {buyerState && isIntraState && (
                      <>
                        <td className="px-2 py-2 text-right text-gray-600">{formatINR(calc?.cgst ?? 0)}</td>
                        <td className="px-2 py-2 text-right text-gray-600">{formatINR(calc?.sgst ?? 0)}</td>
                      </>
                    )}
                    {buyerState && !isIntraState && (
                      <td className="px-2 py-2 text-right text-gray-600">{formatINR(calc?.igst ?? 0)}</td>
                    )}
                    <td className="px-2 py-2 text-right font-medium text-gray-900">
                      {formatINR(calc?.total ?? 0)}
                    </td>
                    <td className="px-2 py-2">
                      <button
                        onClick={() => removeLineItem(item.id)}
                        disabled={lineItems.length <= 1}
                        className="text-gray-400 hover:text-red-500 disabled:opacity-30"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              }).flat()}
              {/* Squad Delta: when loader view is on, render exploded children
                  indented under their bundle parent. Children are read-only —
                  the line-item edit surface is always the parent row. */}
              {bundleRenderMode === 'expand' &&
                lineItems.flatMap((item) =>
                  (item.customFields?.bundleComponents ?? []).map((c, ci) => (
                    <tr
                      key={`${item.id}-c-${ci}`}
                      className="border-b border-gray-50 bg-gray-50/50 text-xs text-gray-500"
                    >
                      <td className="px-2 py-1 pl-8">↳ {c.name} {c.sku ? `(${c.sku})` : ''}</td>
                      <td className="px-2 py-1">-</td>
                      <td className="px-2 py-1 text-right">{c.qty}</td>
                      <td className="px-2 py-1 text-right" colSpan={99}>
                        {c.unit ?? ''}
                      </td>
                    </tr>
                  )),
                )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Document-Level Discount */}
      <div className="mb-4 rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4">
        <div className="flex items-center gap-3 mb-2">
          <h4 className="text-sm font-semibold text-amber-800">Invoice Discount</h4>
          <div className="flex rounded-lg border border-amber-300 overflow-hidden">
            <button onClick={() => setDocDiscountType('amount')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'amount' ? 'bg-amber-600 text-white' : 'text-amber-700 hover:bg-amber-100'}`}>
              &#8377; Amount
            </button>
            <button onClick={() => setDocDiscountType('percentage')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'percentage' ? 'bg-amber-600 text-white' : 'text-amber-700 hover:bg-amber-100'}`}>
              % Percentage
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="0"
            value={docDiscountValue || ''}
            onChange={e => setDocDiscountValue(parseFloat(e.target.value) || 0)}
            placeholder={docDiscountType === 'amount' ? 'Discount amount' : '% Discount'}
            className="w-48 rounded-lg border border-amber-300 px-3 py-2 text-sm"
          />
          <span className="text-sm text-amber-700">
            {docDiscountType === 'amount'
              ? `(${calculations.docDiscountPct?.toFixed(2) || 0}%)`
              : `(${formatINR(calculations.docDiscountAmt || 0)})`}
          </span>
          {(calculations.docDiscountPct ?? 0) > maxDocDiscountPct && (
            <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
              <AlertTriangle size={14} /> Exceeds max {maxDocDiscountPct}% — needs approval
            </span>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wider">Summary</h2>
        <div className="ml-auto w-full max-w-sm space-y-2">
          <SummaryRow label="Subtotal" value={formatINR(calculations.subtotal)} />
          {calculations.totalItemDiscount > 0 && (
            <SummaryRow label="Item Discounts" value={`-${formatINR(calculations.totalItemDiscount)}`} />
          )}
          {calculations.docDiscountAmt > 0 && (
            <SummaryRow label={`Invoice Discount (${calculations.docDiscountPct?.toFixed(1)}%)`} value={`-${formatINR(calculations.docDiscountAmt)}`} />
          )}
          {buyerState && isIntraState && (
            <>
              <SummaryRow label="CGST" value={formatINR(calculations.totalCgst)} />
              <SummaryRow label="SGST" value={formatINR(calculations.totalSgst)} />
            </>
          )}
          {buyerState && !isIntraState && (
            <SummaryRow label="IGST" value={formatINR(calculations.totalIgst)} />
          )}
          <SummaryRow label="Total Tax" value={formatINR(calculations.totalTax)} />
          {calculations.roundOff !== 0 && (
            <SummaryRow label="Round Off" value={formatINR(calculations.roundOff)} />
          )}
          <div className="border-t border-gray-200 pt-2">
            <div className="flex justify-between text-base font-bold text-gray-900">
              <span>Grand Total</span>
              <span>{formatINR(calculations.grandTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sprint 2-3 — Payment Schedule (Agent JJ) */}
      <PaymentSchedulePanel
        invoiceId={id ?? ''}
        invoiceTotal={calculations.grandTotal}
        invoiceIsDraft={isNew}
      />

      {/* Product Grid Selector Modal */}
      <ProductGridSelector
        open={showProductGrid}
        onClose={() => setShowProductGrid(false)}
        onSelect={handleProductSelect}
      />
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}
